//authSlice.ts
import { UserType } from "@/app/shared_enums/enums";
import { ResourceTypes } from "@/app/types/roles";
import {
  createUser,
  customerOtpVerify,
  getUserResource,
  loginEmail,
  loginUser,
  OtpVerify,
} from "@/services/AuthService";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define User and Auth State Interfaces
export interface User {
  token_type: string;
  username?: string;
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  org_id: number;
  org_name?: string;
  role_id?: number;
  role_name?: string;
  counter_id?: number;
  department_id?: number;
  service_id?: number;
  department_name?: string;
  service_name?: string;
}

interface AuthState {
  userToken: string | null;
  userInfo: User | null;
  accessLevels: Record<string, string>;
  sidePanel: string;
  error: string | null;
  loading: boolean;
  isLoggedIn: boolean;
  lastRefetch: number | null;
  isEmailVerify: boolean;
  userType: UserType;
  skipRequests: boolean;
}

// Extract values from localStorage
const userToken = localStorage.getItem("userToken");
const userInfo = localStorage.getItem("userInfo");
const accessLevels = localStorage.getItem("accessLevels");
const sidePanel = localStorage.getItem("sidePanel");
const userPersona = localStorage.getItem("userType") as unknown as UserType;

const initialState: AuthState = {
  userToken,
  userInfo: userInfo ? JSON.parse(userInfo) : null,
  accessLevels: accessLevels ? JSON.parse(accessLevels) : {},
  sidePanel: sidePanel ?? "",
  error: null,
  loading: false,
  isLoggedIn: Boolean(userToken),
  lastRefetch: null,
  isEmailVerify: false,
  userType: userPersona ?? UserType.Admin,
  skipRequests: false, // Add this line
};

// Slice Definition
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateUserInfo: (state, action: PayloadAction<User>) => {
      state.userInfo = action.payload; // Update userInfo in the state
      localStorage.setItem("userInfo", JSON.stringify(action.payload)); // Save to localStorage
    },
    setSkipRequests: (state, action: PayloadAction<boolean>) => {
      state.skipRequests = action.payload; // Action to set the flag
    },
    tokenReceived: (state, action: PayloadAction<string>) => {
      state.userToken = action.payload;
      state.skipRequests = false; // Reset the flag when the user logs in
    },
    logout: (state) => {
      localStorage.removeItem("userToken");
      localStorage.removeItem("userInfo");
      localStorage.removeItem("sidePanel");
      localStorage.removeItem("accessLevels");
      localStorage.removeItem("currentTicket");
      localStorage.removeItem("is_occupied");
      localStorage.removeItem("domain");
      localStorage.removeItem("originalSidePanel");
      localStorage.removeItem("originalSidebarContext");
      localStorage.removeItem("scopedSidebarContext");
      state.userToken = null;
      state.userInfo = null;
      state.error = null;
      state.loading = false;
      state.isLoggedIn = false;
      state.lastRefetch = null;
      localStorage.removeItem("userType");
    },
    updateLastRefetch: (state) => {
      state.lastRefetch = Date.now();
    },
  },
  // Login AsyncThunk
  extraReducers: (builder) => {
    builder
      .addCase(customerLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(customerLogin.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(customerLogin.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload as string;
      })
      .addCase(createSelectedUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSelectedUser.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createSelectedUser.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload as string;
      })
      .addCase(customerOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(customerOtp.fulfilled, (state, { payload }) => {
        const { sidepanel, accessLevels, ...user } = payload.response;
        state.loading = false;
        state.error = null;
        state.isLoggedIn = true;
        state.userInfo = user;
        state.userToken = user.access_token; // Save token
        state.userType = UserType.RemoteUser;
        localStorage.setItem("userType", UserType[UserType.RemoteUser]);
        localStorage.setItem("userToken", payload.response.access_token);
        localStorage.setItem("userInfo", JSON.stringify(payload.response));
      })
      .addCase(customerOtp.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload as string;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(login.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload as string;
      })
      // Login OTP AsyncThunk
      .addCase(loginOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginOtp.fulfilled, (state, { payload }) => {
        const { sidepanel, accessLevels, ...user } = payload.response;
        state.loading = false;
        state.error = null;
        state.isLoggedIn = true;
        state.userInfo = user;
        state.userToken = user.access_token; // Save token
        state.userType = UserType.Admin;
        localStorage.setItem("userType", UserType[UserType.Admin]);
        localStorage.setItem("sidePanel", sidepanel);
        localStorage.setItem("userToken", user.access_token);
        localStorage.setItem("userInfo", JSON.stringify(user));
        localStorage.setItem("accessLevels", JSON.stringify(accessLevels));
      })
      .addCase(loginOtp.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload as string;
      });
  },
});

// Utility function to extract domain from URL and store in localStorage
export const extractAndStoreDomain = (url: string): string => {
  try {
    // Create URL object from the input
    const urlObject = new URL(url);
    
    // Extract hostname (e.g., "www.something.com")
    let domain = urlObject.hostname;
    
    // Remove 'www.' if present
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }
    
    // Store the extracted domain in localStorage
    localStorage.setItem("domain", domain);
    
    return domain;
  } catch (error) {
    // If URL parsing fails, just store the original input
    // This handles cases where the input might already be just a domain
    const cleanDomain = url.replace(/^https?:\/\/(www\.)?/i, '').split('/')[0];
    localStorage.setItem("domain", cleanDomain);
    return cleanDomain;
  }
};

// Define LoginParams and OtpParams Interfaces
interface LoginParams {
  email: string;
  domain:string;
  rememberme:boolean;
}

interface UserLoginParams extends LoginParams {
  org_id: number;
}

interface OtpParams {
  email: string;
  otp: string;
  fcm_token: string;
  its_id:string;
  domain:string;
}

// AsyncThunk for Login
export const login = createAsyncThunk(
  "auth/login",
  async ({ email ,rememberme,domain}: LoginParams, { rejectWithValue }) => {
    try {
      const { data } = await loginEmail(email,domain);
      if (rememberme) {
        localStorage.setItem("email", email);
        localStorage.setItem("domain", domain);
      } else {
        if (localStorage.getItem("email") != null)
          localStorage.removeItem("email");
      }
      return data;
    } catch (error: any) {
      localStorage.removeItem("email");
      return rejectWithValue(
        error.response?.data?.detail || error.message || "Something went wrong"
      );
    }
  }
);

export const customerLogin = createAsyncThunk(
  "auth/CustomerLogin",
  async (
    { identifier, org_id,domain }: { identifier: string; rememberme: boolean ;org_id:number,domain:string},
    { rejectWithValue }
  ) => {
    try {
      const { data } = await loginUser(identifier, org_id,domain);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.message || "Something went wrong"
      );
    }
  }
);


export const createSelectedUser = createAsyncThunk(
  "auth/CreateUser",
  async (
    {
      identifier,
      org_id,
      domain
    }: { identifier: string;org_id: number,domain:string },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await createUser(identifier, org_id,domain);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.message || "Something went wrong"
      );
    }
  }
);

// AsyncThunk for OTP Verification
export const loginOtp = createAsyncThunk(
  "auth/otpVerify",
  async ({ email, otp ,domain}: OtpParams, { rejectWithValue }) => {
    try {
      const {
        data: { response, message },
      } = await OtpVerify(email, otp,domain);

      const { data: userResource } = await getUserResource(
        response.role_id,
        response.access_token
      );

      const payload = response;
      const accessLevels = extractAccessCodes(userResource);

      if (userResource && accessLevels) {
        const sortByIndex = (a: ResourceTypes, b: ResourceTypes) =>
          a.index - b.index;

        // Recursive sorting and filtering function
        const sortAndFilterResources = (
          resources: ResourceTypes[]
        ): ResourceTypes[] => {
          return resources
            .filter((resource) => {
              // Step 1: Filter out resources where the code starts with "hide_"
              if (!resource.show_menu) {
                return false;
              }

              // Step 2: Recursively filter children if they exist
              if (resource.children && resource.children.length > 0) {
                const filteredChildren = sortAndFilterResources(
                  resource.children
                );

                // Assign the filtered children to the parent
                resource.children = filteredChildren;

                // If all children are filtered out, evaluate the parent's `access_type`
                if (
                  filteredChildren.length === 0 &&
                  resource.access_type === "no_access"
                ) {
                  return false;
                }

                // If all filtered children have `access_type` as "no_access", remove the parent
                if (
                  filteredChildren.every(
                    (child) => child.access_type === "no_access"
                  ) &&
                  resource.access_type === "no_access"
                ) {
                  return false;
                }
              } else {
                // Step 3: If no children and `access_type` is "no_access", remove the resource
                if (resource.access_type === "no_access") {
                  return false;
                }
              }

              return true; // Keep the resource if it passes all checks
            })
            .sort(sortByIndex); // Sort by index
        };

        // Step 1: Sort and filter the side panel resources
        const filteredSortedResources = sortAndFilterResources(userResource);
        // Step 1b: Mark Events group as is_scoped so its children render as a scoped sidebar
        const markScopedResources = (resources: ResourceTypes[]): ResourceTypes[] =>
          resources.map((resource) => {
            if (resource.code === "events") {
              return { ...resource, is_scoped: true };
            }
            if (resource.children && resource.children.length > 0) {
              return { ...resource, children: markScopedResources(resource.children) };
            }
            return resource;
          });
        const flattenedResources = markScopedResources(filteredSortedResources);
        // Step 2: Set the side panel data in local storage
        const stringifiedData = JSON.stringify(flattenedResources);
        const encodedData = btoa(stringifiedData);

        // Step 3: Set the side panel data in Redux state
        payload.sidepanel = encodedData;
        payload.accessLevels = accessLevels;
      }

      return { response: payload, message }; // Ensure response has a `response` property with user data
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.message || "Something went wrong"
      );
    }
  }
);

export const customerOtp = createAsyncThunk(
  "auth/CustomerOtpVerify",
  async (
    { email, otp, fcm_token,its_id ,domain}: OtpParams,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const {
        data: { response, message },
      } = await customerOtpVerify(email, otp, fcm_token,its_id,domain);
      const userInfo: User = {
        token_type: response.token_type,
        id: response.id,
        first_name: response.first_name,
        last_name: response.last_name,
        email: response.email,
        org_id: response.org_id,
        username: response.username,
      };

      dispatch(updateUserInfo(userInfo)); // Dispatch the action to update userInfo
      localStorage.setItem("userInfo", JSON.stringify(userInfo));
      const payload = response;
      return { response: payload, message }; // Ensure response has a `response` property with user data
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.message || "Something went wrong"
      );
    }
  }
);

export const { logout, tokenReceived, updateLastRefetch } = authSlice.actions;
export default authSlice.reducer;

function extractAccessCodes(data: any) {
  let accessMap: Record<string, string> = {};

  function processItem(item: Record<string, any>) {
    const accessType: string = item.access_type;

    if (accessType) {
      accessMap[item.code] = accessType;
    }

    if (item.children && item.children.length > 0) {
      item.children.forEach(processItem);
    }
  }

  data.forEach(processItem);

  return accessMap;
}

export const { updateUserInfo } = authSlice.actions;